import { useState } from "react";
import { SlidersHorizontal, Check } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import * as Checkbox from "@radix-ui/react-checkbox";
import { useTranslation } from "@/packages/utils";
import type { TimeRange, KpiId } from "./dashboard.data";
import { ALL_KPI_IDS } from "./dashboard.data";

// ── Types ────────────────────────────────────────────────────────

interface DashboardHeaderProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  visibleKpis: KpiId[];
  onVisibleKpisChange: (ids: KpiId[]) => void;
}

// ── Time Range Options ───────────────────────────────────────────

const TIME_RANGE_OPTIONS: { id: TimeRange; labelKey: string }[] = [
  { id: "weekly", labelKey: "dashboard:timeRange.weekly" },
  { id: "monthly", labelKey: "dashboard:timeRange.monthly" },
  { id: "yearly", labelKey: "dashboard:timeRange.yearly" },
];

// ── KPI Label Keys ───────────────────────────────────────────────

const KPI_LABEL_KEYS: Record<KpiId, string> = {
  activeAudits: "dashboard:kpi.activeAudits",
  completedAudits: "dashboard:kpi.completed",
  openFindings: "dashboard:kpi.openFindings",
  missingEvidence: "dashboard:kpi.missingEvidence",
  fixRate: "dashboard:kpi.fixRate",
  upcomingDeadlines: "dashboard:kpi.upcomingDeadlines",
};

// ── TimeRangeControl ─────────────────────────────────────────────

export function TimeRangeControl({
  timeRange,
  onTimeRangeChange,
}: {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}) {
  const { t } = useTranslation(["dashboard"]);

  return (
    <div className="flex items-center gap-1 rounded-lg bg-surface p-1">
      {TIME_RANGE_OPTIONS.map((option) => {
        const isActive = timeRange === option.id;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onTimeRangeChange(option.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-accent text-white"
                : "text-secondary hover:text-primary hover:bg-surface"
            }`}
          >
            {t(option.labelKey as "dashboard:timeRange.weekly")}
          </button>
        );
      })}
    </div>
  );
}

// ── KpiFilterPopover ─────────────────────────────────────────────

export function KpiFilterPopover({
  visibleKpis,
  onVisibleKpisChange,
  onResetOrder,
}: {
  visibleKpis: KpiId[];
  onVisibleKpisChange: (ids: KpiId[]) => void;
  onResetOrder?: () => void;
}) {
  const { t } = useTranslation(["dashboard"]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<KpiId[]>(visibleKpis);

  const handleOpen = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(visibleKpis);
    }
    setOpen(nextOpen);
  };

  const handleToggle = (id: KpiId, checked: boolean) => {
    if (checked) {
      if (draft.length < 6) {
        setDraft([...draft, id]);
      }
    } else {
      if (draft.length > 1) {
        setDraft(draft.filter((k) => k !== id));
      }
    }
  };

  const handleApply = () => {
    onVisibleKpisChange(draft);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-secondary hover:text-primary hover:bg-surface transition-colors"
          title={t("dashboard:kpiFilter.title")}
        >
          <SlidersHorizontal size={14} />
          <span className="text-sm">Filter</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          className="z-50 w-56 rounded-xl border border-border bg-card p-3 shadow-lg"
        >
          <p className="text-xs font-semibold text-secondary mb-2">
            {t("dashboard:kpiFilter.title")}
          </p>

          <div className="flex flex-col gap-1.5">
            {ALL_KPI_IDS.map((id) => {
              const isChecked = draft.includes(id);
              const isLastChecked = isChecked && draft.length === 1;
              const isMaxReached = !isChecked && draft.length >= 6;
              const isDisabled = isLastChecked || isMaxReached;

              return (
                <label
                  key={id}
                  htmlFor={`kpi-filter-${id}`}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:bg-surface"
                  }`}
                >
                  <Checkbox.Root
                    id={`kpi-filter-${id}`}
                    checked={isChecked}
                    disabled={isDisabled}
                    onCheckedChange={(checked) =>
                      handleToggle(id, checked === true)
                    }
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface transition-colors data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                  >
                    <Checkbox.Indicator>
                      <Check size={12} className="text-white" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <span className="text-primary text-sm">
                    {t(KPI_LABEL_KEYS[id] as "dashboard:kpi.activeAudits")}
                  </span>
                </label>
              );
            })}
          </div>

          {draft.length >= 6 && (
            <p className="text-xs text-muted mt-2">
              {t("dashboard:kpiFilter.maxReached")}
            </p>
          )}

          {onResetOrder && (
            <button
              type="button"
              onClick={() => {
                onResetOrder();
              }}
              className="mt-2 w-full text-left text-xs text-secondary hover:text-primary transition-colors px-2 py-1 rounded"
            >
              {t("dashboard:kpiFilter.resetOrder")}
            </button>
          )}

          <button
            type="button"
            onClick={handleApply}
            className="mt-3 w-full rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-accent/90"
          >
            {t("dashboard:kpiFilter.apply")}
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ── DashboardHeader ──────────────────────────────────────────────

export function DashboardHeader({
  timeRange,
  onTimeRangeChange,
  visibleKpis,
  onVisibleKpisChange,
}: DashboardHeaderProps) {
  const { t } = useTranslation(["dashboard"]);

  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
      <h1 className="text-lg font-semibold text-primary">
        {t("dashboard:title")}
      </h1>
      <div className="flex items-center gap-2">
        <TimeRangeControl
          timeRange={timeRange}
          onTimeRangeChange={onTimeRangeChange}
        />
        <KpiFilterPopover
          visibleKpis={visibleKpis}
          onVisibleKpisChange={onVisibleKpisChange}
        />
      </div>
    </div>
  );
}
